import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarDays,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Target,
  DollarSign,
  Scissors,
  Palette,
  Send,
  BarChart3,
  Calendar as CalendarIcon,
  Pencil,
  FileText,
  Clock,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from "date-fns";

const ITEM_TYPES = [
  { key: "editing", label: "Video Editing", icon: Scissors, color: "bg-violet-100 text-violet-700 border-violet-200" },
  { key: "graphics", label: "Graphics & Design", icon: Palette, color: "bg-blue-100 text-blue-700 border-blue-200" },
  { key: "posting", label: "Social Posting", icon: Send, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { key: "ads", label: "Paid Ads", icon: BarChart3, color: "bg-amber-100 text-amber-700 border-amber-200" },
];

export default function ContentCalendarPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDoc, setCalendarDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [strategyTheme, setStrategyTheme] = useState("");
  const [targetKpis, setTargetKpis] = useState("");
  const [adSpendBudget, setAdSpendBudget] = useState(0);

  // Add Item Modal
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [selectedDayStr, setSelectedDayStr] = useState("");
  const [itemForm, setItemForm] = useState({
    type: "posting",
    title: "",
    status: "planned",
    assigned_to: "",
    notes: "",
  });

  // View / Edit Existing Item Details Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [editItemForm, setEditItemForm] = useState({
    title: "",
    type: "posting",
    status: "planned",
    notes: "",
  });
  const [updatingItem, setUpdatingItem] = useState(false);

  const yearMonth = useMemo(() => format(currentDate, "yyyy-MM"), [currentDate]);

  // Load clients list
  useEffect(() => {
    apiClient
      .get("/clients")
      .then((res) => {
        setClients(res.data);
        if (res.data.length > 0) {
          setSelectedClientId(res.data[0].id);
        }
      })
      .catch(() => toast.error("Failed to load clients list"));
  }, []);

  // Fetch calendar document whenever client or yearMonth changes
  useEffect(() => {
    if (!selectedClientId) return;
    setLoading(true);
    apiClient
      .get(`/content-calendars?client_id=${selectedClientId}&year_month=${yearMonth}`)
      .then((res) => {
        if (res.data && res.data.length > 0) {
          const doc = res.data[0];
          setCalendarDoc(doc);
          setStrategyTheme(doc.strategy_theme || "");
          setTargetKpis(doc.target_kpis || "");
          setAdSpendBudget(doc.ad_spend_budget || 0);
        } else {
          setCalendarDoc(null);
          setStrategyTheme("");
          setTargetKpis("");
          setAdSpendBudget(0);
        }
      })
      .catch(() => toast.error("Could not load content calendar"))
      .finally(() => setLoading(false));
  }, [selectedClientId, yearMonth]);

  const handleSaveStrategy = async (e) => {
    e.preventDefault();
    setSavingStrategy(true);
    try {
      const payload = {
        client_id: selectedClientId,
        year_month: yearMonth,
        strategy_theme: strategyTheme,
        target_kpis: targetKpis,
        ad_spend_budget: parseFloat(adSpendBudget) || 0,
        items: calendarDoc?.items || [],
      };
      const res = await apiClient.post("/content-calendars", payload);
      setCalendarDoc(res.data);
      toast.success("Monthly Strategy Saved!");
    } catch {
      toast.error("Failed to save strategy");
    } finally {
      setSavingStrategy(false);
    }
  };

  const handleOpenAddItem = (dateStr) => {
    setSelectedDayStr(dateStr);
    setItemForm({
      type: "posting",
      title: "",
      status: "planned",
      assigned_to: "",
      notes: "",
    });
    setItemModalOpen(true);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!calendarDoc?.id) {
      try {
        const initPayload = {
          client_id: selectedClientId,
          year_month: yearMonth,
          strategy_theme: strategyTheme,
          target_kpis: targetKpis,
          ad_spend_budget: parseFloat(adSpendBudget) || 0,
          items: [],
        };
        const created = await apiClient.post("/content-calendars", initPayload);
        const itemPayload = { ...itemForm, date: selectedDayStr };
        await apiClient.post(`/content-calendars/${created.data.id}/items`, itemPayload);
        toast.success("Calendar item scheduled");
        setItemModalOpen(false);
        const refetched = await apiClient.get(`/content-calendars?client_id=${selectedClientId}&year_month=${yearMonth}`);
        setCalendarDoc(refetched.data[0]);
      } catch {
        toast.error("Failed to schedule item");
      }
      return;
    }

    try {
      const itemPayload = { ...itemForm, date: selectedDayStr };
      await apiClient.post(`/content-calendars/${calendarDoc.id}/items`, itemPayload);
      toast.success("Calendar item scheduled");
      setItemModalOpen(false);
      const refetched = await apiClient.get(`/content-calendars?client_id=${selectedClientId}&year_month=${yearMonth}`);
      setCalendarDoc(refetched.data[0]);
    } catch {
      toast.error("Failed to schedule item");
    }
  };

  const handleOpenItemDetails = (item, e) => {
    e.stopPropagation();
    setActiveItem(item);
    setEditItemForm({
      title: item.title || "",
      type: item.type || "posting",
      status: item.status || "planned",
      notes: item.notes || "",
    });
    setDetailModalOpen(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!calendarDoc?.id || !activeItem?.id) return;
    setUpdatingItem(true);
    try {
      await apiClient.patch(`/content-calendars/${calendarDoc.id}/items/${activeItem.id}`, editItemForm);
      toast.success("Deliverable details updated!");
      setDetailModalOpen(false);
      const refetched = await apiClient.get(`/content-calendars?client_id=${selectedClientId}&year_month=${yearMonth}`);
      setCalendarDoc(refetched.data[0]);
    } catch {
      toast.error("Failed to update deliverable");
    } finally {
      setUpdatingItem(false);
    }
  };

  const handleDeleteItem = async (itemId, e) => {
    e.stopPropagation();
    if (!calendarDoc?.id) return;
    try {
      await apiClient.delete(`/content-calendars/${calendarDoc.id}/items/${itemId}`);
      toast.success("Item removed");
      if (detailModalOpen && activeItem?.id === itemId) {
        setDetailModalOpen(false);
      }
      const refetched = await apiClient.get(`/content-calendars?client_id=${selectedClientId}&year_month=${yearMonth}`);
      setCalendarDoc(refetched.data[0]);
    } catch {
      toast.error("Failed to remove item");
    }
  };

  // Days calculation
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart); // 0 = Sun, 1 = Mon...

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  return (
    <div className="space-y-6" data-testid="content-calendar-page">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Client monthly roadmap for Video Editing, Graphics, Posting, and Ads
          </p>
        </div>

        {/* Client selector & Month nav */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm"
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.business})
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1 bg-muted/60 p-1 rounded-lg border">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-semibold px-2 text-sm">
              {format(currentDate, "MMMM yyyy")}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Monthly Strategy & Themes Hub ── */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Monthly Content Strategy & Campaign Focus — {format(currentDate, "MMMM yyyy")}
            </CardTitle>
            <Badge variant="outline" className="text-xs">
              {selectedClient?.name || "Client"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveStrategy} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1 mb-1">
                <Target className="w-3.5 h-3.5 text-primary" /> Strategy / Campaign Theme
              </Label>
              <Input
                placeholder="e.g. Festive Collection Launch & Testimonial Sprint"
                value={strategyTheme}
                onChange={(e) => setStrategyTheme(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1 mb-1">
                <Target className="w-3.5 h-3.5 text-emerald-600" /> Target KPIs & Goals
              </Label>
              <Input
                placeholder="e.g. 50K Reach, 150 Inquiries, 3.5x ROAS"
                value={targetKpis}
                onChange={(e) => setTargetKpis(e.target.value)}
              />
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs font-semibold flex items-center gap-1 mb-1">
                  <DollarSign className="w-3.5 h-3.5 text-amber-600" /> Ad Spend Budget
                </Label>
                <Input
                  type="number"
                  placeholder="e.g. 50000"
                  value={adSpendBudget}
                  onChange={(e) => setAdSpendBudget(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={savingStrategy} className="shrink-0 h-10">
                {savingStrategy ? "Saving…" : "Save Strategy"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Category Legend ── */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="font-semibold text-muted-foreground mr-1">Workstreams:</span>
        {ITEM_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <Badge key={t.key} className={`${t.color} flex items-center gap-1.5`}>
              <Icon className="w-3 h-3" />
              {t.label}
            </Badge>
          );
        })}
      </div>

      {/* ── Monthly Interactive Grid ── */}
      <div className="rounded-xl border bg-card overflow-hidden shadow-sm">
        {/* Days of week header */}
        <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-xs font-semibold py-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day}>{day}</div>
          ))}
        </div>

        {/* Days cells */}
        <div className="grid grid-cols-7 auto-rows-fr divide-x divide-y">
          {/* Empty cells before 1st of month */}
          {Array.from({ length: startDayOfWeek }).map((_, idx) => (
            <div key={`empty-${idx}`} className="min-h-[110px] bg-muted/10 p-1.5" />
          ))}

          {/* Actual days */}
          {daysInMonth.map((day) => {
            const dateStr = format(day, "yyyy-MM-dd");
            const dayNumber = format(day, "d");
            const isToday = format(new Date(), "yyyy-MM-dd") === dateStr;
            const itemsForDay = (calendarDoc?.items || []).filter((it) => it.date === dateStr);

            return (
              <div
                key={dateStr}
                onClick={() => handleOpenAddItem(dateStr)}
                className={`min-h-[110px] p-1.5 hover:bg-muted/30 cursor-pointer transition-colors flex flex-col justify-between group ${
                  isToday ? "bg-primary/5 font-semibold" : ""
                }`}
              >
                {/* Day Header */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isToday
                        ? "bg-primary text-primary-foreground font-bold"
                        : "text-muted-foreground"
                    }`}
                  >
                    {dayNumber}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenAddItem(dateStr);
                    }}
                  >
                    <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </div>

                {/* Items Container */}
                <div className="flex-1 space-y-1 my-1 overflow-hidden">
                  {itemsForDay.map((item) => {
                    const typeConfig = ITEM_TYPES.find((t) => t.key === item.type) || ITEM_TYPES[0];
                    const Icon = typeConfig.icon;
                    return (
                      <div
                        key={item.id}
                        onClick={(e) => handleOpenItemDetails(item, e)}
                        className={`text-[11px] p-1 rounded border flex items-center justify-between gap-1 group/item transition-all hover:shadow-sm hover:scale-[1.02] cursor-pointer ${typeConfig.color}`}
                        title="Click to view/edit deliverable details"
                      >
                        <div className="flex items-center gap-1 truncate">
                          <Icon className="w-3 h-3 shrink-0" />
                          <span className="truncate font-medium">{item.title}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteItem(item.id, e)}
                          className="opacity-0 group-hover/item:opacity-100 hover:text-red-700 shrink-0 p-0.5"
                          title="Delete item"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Add Item Dialog ── */}
      <Dialog open={itemModalOpen} onOpenChange={setItemModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary" />
              Schedule Content Item — {selectedDayStr}
            </DialogTitle>
            <DialogDescription>
              Assign deliverable to Video Editing, Graphics, Social Posting, or Ads.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddItem} className="space-y-4 pt-2">
            <div>
              <Label>Workstream / Category *</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                value={itemForm.type}
                onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })}
              >
                {ITEM_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Deliverable Title *</Label>
              <Input
                className="mt-1"
                placeholder="e.g. Gold Necklace Carousel #3 or Hook Reel Edit"
                value={itemForm.title}
                onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Status</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                value={itemForm.status}
                onChange={(e) => setItemForm({ ...itemForm, status: e.target.value })}
              >
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <Label>Notes & Specifics</Label>
              <Input
                className="mt-1"
                placeholder="Aspect ratio, target ad spend, creative copy..."
                value={itemForm.notes}
                onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setItemModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Add to Calendar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── View / Edit Deliverable Details Modal ── */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-primary" />
              Deliverable Details — {activeItem?.date}
            </DialogTitle>
            <DialogDescription>
              View, edit, or update status for this scheduled deliverable.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateItem} className="space-y-4 pt-2">
            <div>
              <Label>Workstream / Category</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                value={editItemForm.type}
                onChange={(e) => setEditItemForm({ ...editItemForm, type: e.target.value })}
              >
                {ITEM_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Deliverable Title *</Label>
              <Input
                className="mt-1"
                value={editItemForm.title}
                onChange={(e) => setEditItemForm({ ...editItemForm, title: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Current Status</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                value={editItemForm.status}
                onChange={(e) => setEditItemForm({ ...editItemForm, status: e.target.value })}
              >
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <Label>Notes & Specifics</Label>
              <textarea
                className="w-full mt-1 p-2.5 rounded-md border border-input bg-background text-sm outline-none focus:ring-1 focus:ring-primary min-h-[70px]"
                placeholder="Aspect ratio, design rules, creative copy..."
                value={editItemForm.notes}
                onChange={(e) => setEditItemForm({ ...editItemForm, notes: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={(e) => handleDeleteItem(activeItem?.id, e)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>

              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setDetailModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={updatingItem}>
                  {updatingItem ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
